"use client";

import { DateTime } from "luxon";

import { Badge } from "@/src/components/shadcn/ui/badge";
import type { FhirOrder } from "@/src/lib/fhir-types";
import type { OrderMatchOutput } from "@/src/lib/workflows/steps/ehr/order-match";

function formatDate(dateString: string | null): string | null {
  if (!dateString) return null;

  // Date-only strings: split directly to avoid UTC midnight shifting to previous day
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-");

    return `${month}/${day}/${year}`;
  }

  // Full ISO timestamps: read in UTC so the calendar date matches what was stored
  const dt = DateTime.fromISO(dateString, { zone: "utc" });

  return dt.isValid ? dt.toFormat("MM/dd/yyyy") : dateString;
}

interface OrderMatchPanelProps {
  match: OrderMatchOutput | undefined;
}

export function OrderMatchPanelView({ match }: OrderMatchPanelProps) {
  if (!match) return null;

  if (match.matchedOrder) {
    return (
      <div className="flex flex-col gap-2 mt-2">
        <DocumentContextSection labContext={match.labContext} />
        <p>
          <strong>Matched Order (verify before completing):</strong>
        </p>
        <OrderSummaryRow order={match.matchedOrder} />
      </div>
    );
  }

  const candidates = match.candidateOrders ?? [];

  if (candidates.length === 0) {
    return (
      <div className="flex flex-col gap-2 mt-2">
        <DocumentContextSection labContext={match.labContext} />
        <p>
          <strong>Candidate Orders:</strong>
        </p>
        <p className="text-muted-foreground italic">None in the matching date window.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 mt-2">
      <DocumentContextSection labContext={match.labContext} />
      <p>
        <strong>Candidate Orders (verify none match):</strong>
      </p>
      <div className="flex flex-col gap-3">
        {candidates.map((candidate, idx) => (
          <OrderSummaryRow key={candidate.id} order={candidate} index={idx + 1} />
        ))}
      </div>
    </div>
  );
}

function DocumentContextSection({
  labContext,
}: {
  labContext: { labNames: string[]; dateOfEvent: string | null } | undefined;
}) {
  if (!labContext) return null;

  const lines: Array<{ label: string; value: string | null }> = [
    { label: "Test name", value: labContext.labNames.length > 0 ? labContext.labNames.join(", ") : null },
    { label: "Date of event", value: formatDate(labContext.dateOfEvent) },
  ];

  const visibleLines = lines.filter(({ value }) => value);

  if (visibleLines.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 mb-1">
      <p>
        <strong>Document:</strong>
      </p>
      <div className="pl-4 border-l-2 border-blue-500/30">
        {visibleLines.map(({ label, value }) => (
          <p key={label} className="whitespace-pre-line">
            <strong>{label}:</strong> {value}
          </p>
        ))}
      </div>
    </div>
  );
}

function OrderSummaryRow({ order, index }: { order: FhirOrder; index?: number }) {
  const lines: Array<{ label: string; value: string | null }> = [
    { label: "Order", value: order.orderName },
    { label: "Order date", value: formatDate(order.orderDate) },
    { label: "Collection date", value: formatDate(order.collectionDate) },
  ];

  return (
    <div className="pl-4 border-l-2 border-green-500/30">
      {index != null && (
        <p className="flex items-center gap-2">
          <strong>#{index}</strong>
          {order.ecwStatus && <Badge variant="secondary">{order.ecwStatus}</Badge>}
        </p>
      )}
      {index == null && order.ecwStatus && (
        <p>
          <Badge variant="secondary">{order.ecwStatus}</Badge>
        </p>
      )}
      {lines.map(({ label, value }) =>
        value ? (
          <p key={label} className="whitespace-pre-line">
            <strong>{label}:</strong> {value}
          </p>
        ) : null
      )}
    </div>
  );
}
